import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { auth } from '../lib/firebase';
import {
  getGetConversationQueryKey,
  getListConversationsQueryKey,
} from '../api/generated/generated';

const API_URL = import.meta.env.VITE_API_URL || '';

export interface StreamChatMeta {
  conversation_id: string;
  message_id: string;
}

export interface StreamChatUsage {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
}

/**
 * Parse named SSE events from a text buffer.
 * Returns parsed events and any remaining incomplete buffer.
 */
function parseSSEEvents(buffer: string): {
  events: Array<{ event: string; data: string }>;
  remaining: string;
} {
  const events: Array<{ event: string; data: string }> = [];
  const blocks = buffer.split('\n\n');
  // The last element may be incomplete
  const remaining = blocks.pop() ?? '';

  for (const block of blocks) {
    if (!block.trim()) continue;
    let eventType = 'message';
    let data = '';
    for (const line of block.split('\n')) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        data = line.slice(6);
      }
    }
    if (data) {
      events.push({ event: eventType, data });
    }
  }

  return { events, remaining };
}

export function useStreamChat(conversationId: string | null) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<StreamChatMeta | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const queryClient = useQueryClient();

  const abort = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const sendMessage = useCallback(
    async (content: string) => {
      if (!conversationId) return;

      setIsStreaming(true);
      setStreamingContent('');
      setError(null);
      setMeta(null);

      const controller = new AbortController();
      abortRef.current = controller;
      let accumulated = '';

      try {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          throw new Error('Not authenticated');
        }
        const token = await currentUser.getIdToken();

        const response = await fetch(
          `${API_URL}/api/v1/conversations/${conversationId}/messages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ content }),
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          const text = await response.text();
          throw new Error(text || `HTTP ${response.status}`);
        }

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const { events, remaining } = parseSSEEvents(buffer);
          buffer = remaining;

          for (const evt of events) {
            const parsed = JSON.parse(evt.data);

            switch (evt.event) {
              case 'meta':
                setMeta(parsed);
                break;
              case 'chunk':
                accumulated += parsed.content;
                setStreamingContent(accumulated);
                break;
              case 'done':
                // Stream complete — invalidate to load persisted messages + auto-title
                queryClient.invalidateQueries({
                  queryKey: getGetConversationQueryKey(conversationId!),
                });
                queryClient.invalidateQueries({
                  queryKey: getListConversationsQueryKey(),
                });
                break;
              case 'error':
                setError(parsed.error || 'Stream error');
                break;
            }
          }
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          // User aborted — not an error
          return;
        }
        const message = err instanceof Error ? err.message : 'Stream failed';
        setError(message);
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [conversationId, queryClient]
  );

  return { sendMessage, isStreaming, streamingContent, error, meta, abort };
}
